/**
 * Resolve `{{$item.foo}}` / `{{$index}}` style templates in a payload object
 * using the loop variables from the iteration's execution context.
 */
export function resolvePayloadTemplates(
  payload: Record<string, unknown> | undefined,
  loopVars: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!payload || !loopVars) return payload;

  // Bind to a const so TypeScript narrows it as non-undefined inside the closure
  const vars = loopVars;

  function resolve(value: unknown): unknown {
    if (typeof value === "string") {
      // Full-value template: "{{$item.tokenAddress}}"
      const match = value.match(/^\{\{(.+?)\}\}$/);
      if (match) {
        const resolved = getByPath(vars, match[1].trim());
        return resolved !== undefined ? resolved : value;
      }
      return value;
    }
    if (Array.isArray(value)) return value.map(resolve);
    if (value && typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) out[k] = resolve(v);
      return out;
    }
    return value;
  }

  return resolve(payload) as Record<string, unknown>;
}

function getByPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}
